import { useState } from "react";
import { Link } from "react-router";
import { admin } from "../../api/client";
import type { ReconciliationIssue } from "../../api/types";
import { can, staffRole, useMe } from "../../auth/session";
import { TocaPage } from "../../components/Shell";
import { Empty, ErrorBox, Field, JsonView, Modal, Pill, Spinner, errorMessage, useToast } from "../../components/ui";
import { useAsync } from "../../hooks/useAsync";
import { fmt, t } from "../../i18n";
import { dateBR, dateTimeShort, timeHM } from "../../lib/format";
import { brl } from "../../lib/money";

export function TocaReconciliacao() {
  const me = useMe();
  const toast = useToast();
  const allowed = can.reconcile(staffRole(me));
  const runs = useAsync(() => admin.reconciliationRuns(), []);
  const issues = useAsync(() => admin.reconciliationIssues(true), []);
  const [day, setDay] = useState("");
  const [running, setRunning] = useState(false);
  const [resolving, setResolving] = useState<ReconciliationIssue | null>(null);
  const [resolution, setResolution] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const last = runs.data?.[0];

  async function runNow() {
    setRunning(true);
    try {
      await admin.reconciliationRun(day || undefined);
      toast(t.toca.reconRan);
      setTimeout(() => { void runs.reload(); void issues.reload(); }, 1500);
    } catch (e) {
      toast(errorMessage(e));
    } finally {
      setRunning(false);
    }
  }

  async function resolve() {
    if (!resolving) return;
    setErr(null);
    if (!resolution.trim()) return setErr(t.toca.noteRequired);
    try {
      await admin.resolveIssue(resolving.id, resolution.trim());
      toast(t.toca.reconResolved);
      setResolving(null);
      setResolution("");
      void issues.reload();
      void runs.reload();
    } catch (e) {
      setErr(errorMessage(e));
    }
  }

  return (
    <TocaPage
      title={t.toca.reconTitle}
      actions={
        allowed ? (
          <div className="row" style={{ gap: 8 }}>
            <label className="sr-only" htmlFor="recon-day">{t.toca.reconDay}</label>
            <div className="inp" style={{ minHeight: 44 }}>
              <input id="recon-day" type="date" value={day} onChange={(e) => setDay(e.target.value)} />
            </div>
            <button type="button" className="btn" onClick={runNow} disabled={running}>{running ? t.toca.reconRunning : t.toca.reconRun}</button>
          </div>
        ) : undefined
      }
    >
      <div className="two n400">
        <div className="stack">
          {runs.error ? <ErrorBox error={runs.error} onRetry={runs.reload} /> : null}
          {last && (
            <div className="card pad stack s8">
              <div className="row between">
                <span className="lbl">{t.toca.reconTitle} · {dateBR(last.day)}</span>
                <Pill tone={last.finishedAt ? (last.issueCount ? "crit" : "ok") : "warn"}>{last.finishedAt ? t.toca.reconClosed : t.toca.reconOpen}</Pill>
              </div>
              <span className="bigval" style={{ color: last.issueCount ? "var(--crit)" : "var(--musgo)" }}>{last.issueCount === 0 ? t.toca.kpiReconOk : fmt(t.toca.kpiReconIssues, { n: last.issueCount })}</span>
              <span className="muted small">rodou às {timeHM(last.finishedAt ?? last.startedAt)} · {last.pixCount} Pix × {last.orderCount} pedidos × {last.execCount} execuções</span>
              <div className="stack s8" style={{ marginTop: 8, fontSize: 13 }}>
                <span className="lbl">{t.toca.reconMatch}</span>
                <div className="kv"><span className="k">{t.toca.reconPix}</span><span className="v mono">{last.pixCount} · {brl(last.pixCents)}</span></div>
                <div className="kv"><span className="k">{t.toca.reconOrders}</span><span className="v mono">{last.orderCount} · {brl(last.orderCents)}</span></div>
                <div className="kv"><span className="k">{t.toca.reconExec}</span><span className="v mono">{last.execCount}</span></div>
                <div className="kv"><span className="k">{t.toca.reconFees}</span><span className="v mono">{brl(last.feesCents)}</span></div>
                <div className="kv"><span className="k">{t.toca.reconRevenue}</span><span className="v mono">{brl(last.revenueCents)}</span></div>
              </div>
              {last.balances && (
                <div className="stack s8" style={{ marginTop: 8, fontSize: 13 }}>
                  <span className="lbl">{t.toca.reconBalances}</span>
                  {Object.entries(last.balances).map(([k, v]) => (
                    <div className="kv" key={k}><span className="k mono">{k}</span><span className="v mono">{/^-?\d+$/.test(String(v)) ? brl(String(v)) : String(v)}</span></div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="card">
            <div className="card-head"><h2>{t.toca.reconRuns}</h2></div>
            {runs.loading && !runs.data ? (
              <Spinner />
            ) : !runs.data || runs.data.length === 0 ? (
              <Empty body={t.toca.kpiReconNone} />
            ) : (
              <div className="tscroll" tabIndex={0}>
                <table>
                  <thead><tr><th>{t.toca.reconDay}</th><th>{t.common.status}</th><th>Pix</th><th>{t.nav.tocaPedidos}</th><th>Exec.</th><th className="r">{t.toca.reconRevenue}</th><th className="r">Issues</th></tr></thead>
                  <tbody>
                    {runs.data.map((r) => (
                      <tr key={r.id}>
                        <td className="mono">{dateBR(r.day)}</td>
                        <td><Pill tone={r.finishedAt ? (r.issueCount ? "crit" : "ok") : "warn"}>{r.finishedAt ? t.toca.reconClosed : t.toca.reconOpen}</Pill></td>
                        <td className="mono">{r.pixCount} · {brl(r.pixCents)}</td>
                        <td className="mono">{r.orderCount} · {brl(r.orderCents)}</td>
                        <td className="mono">{r.execCount}</td>
                        <td className="mono r">{brl(r.revenueCents)}</td>
                        <td className="mono r">{r.issueCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        <div className="card">
          <div className="card-head"><h2>{t.toca.reconIssues}</h2><Pill tone={issues.data?.length ? "crit" : "ok"}>{issues.data?.length ?? 0}</Pill></div>
          {issues.loading && !issues.data ? (
            <Spinner />
          ) : issues.error ? (
            <div style={{ padding: 16 }}><ErrorBox error={issues.error} onRetry={issues.reload} /></div>
          ) : !issues.data || issues.data.length === 0 ? (
            <Empty body={t.toca.reconIssuesNone} />
          ) : (
            <div className="stack" style={{ padding: 16 }}>
              {issues.data.map((i) => (
                <div key={i.id} className="card pad stack s8" style={{ padding: 14 }}>
                  <div className="row between">
                    <Pill tone="crit">{i.kind}</Pill>
                    <span className="muted small">{dateTimeShort(i.createdAt)}</span>
                  </div>
                  {i.orderId && <Link to={`/toca/pedidos/${i.orderId}`} className="mono" style={{ fontSize: 13 }}>{i.orderId}</Link>}
                  <JsonView title="detail" value={i.detail} />
                  {allowed && <div><button type="button" className="btn dark xs" onClick={() => setResolving(i)}>{t.toca.reconResolve}</button></div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {resolving && (
        <Modal title={`${t.toca.reconResolve} · ${resolving.kind}`} onClose={() => setResolving(null)}>
          {resolving.orderId && <span className="mono">{resolving.orderId}</span>}
          <Field label={t.toca.reconResolution}>
            {(id) => <div className="inp"><textarea id={id} value={resolution} onChange={(e) => setResolution(e.target.value)} placeholder={t.common.notePlaceholder} /></div>}
          </Field>
          {err && <div className="form-error" role="alert">{err}</div>}
          <div className="row" style={{ justifyContent: "flex-end" }}>
            <button type="button" className="btn ghost" onClick={() => setResolving(null)}>{t.common.cancel}</button>
            <button type="button" className="btn" onClick={resolve}>{t.common.confirm}</button>
          </div>
        </Modal>
      )}
    </TocaPage>
  );
}
